/* eslint-disable camelcase */

import {
  CardAttributeUpdateAction,
  CardAttributeUpdateActionsEnum,
} from '../../helpers/models/cardAttributeUpdateHelpers';
import {
  isAutoGlobalCard,
  isEnhancedGlobalCard,
  isEnhancedPrivateCard,
} from '../../helpers/models/cardHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {AccountModel} from '../../models/api/accountModel';
import {CardGroupModel} from '../../models/api/cardGroupModel';
import {CardModel} from '../../models/api/cardModel';
import {ContactSourcesEnum, parseContactUid} from '../../models/api/contactModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {ContactDetailsErrorTypesEnum} from '../../store/panels/app/banners/bannersState';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  AccountDetailsHoverOptionsEnum,
  AccountDetailsOpenConversationSectionsEnum,
} from '../accounts/tracking/accountDetailsTrackingHelpers';

/*
 * Constants.
 */

// Controls how long someone would have to hover over the item to be reported.
export const contactDetailsHoverOverTimeToReport = 1000;

export enum ContactDetailsCardNoteActionsEnum {
  EDIT = 'edit',
  ADD = 'add',
  DELETE = 'delete',
}

enum ContactDetailsCardNoteAccessSettingsEnum {
  COMPANY = 'company',
  PERSONAL = 'personal',
  TEAM = 'team',
}

export enum ContactDetailsCardGroupActionsEnum {
  ADD = 'add',
  DELETE = 'delete',
}

export enum ContactDetailsClickActionsEnum {
  MERGE = 'click_merge',
  POPOUT = 'pop_out',
  CLOSE = 'close',
  COPY = 'copy_info',
  COMPOSE = 'click_compose',
  SEARCH_ALL_CONVOS = 'search_all_convos',
  VIEW_DUPLICATE = 'view_duplicate',
  COPY_ACCOUNT_LINK = 'copy_account_link',
}

export enum ContactDetailsClickLocationsEnum {
  HEADER = 'header',
  BODY = 'body',
  TOOLTIP = 'tooltip',
}

export enum ContactDetailsContactTypesEnum {
  PRIVATE = 'private',
  COMPANY = 'company',
}

export enum ContactDetailsOpenConversationSectionsEnum {
  LAST_7D = 'last_7d',
  OLDER = 'older',
  NONE = 'none',
}

export enum ContactDetailsEditActionsEnum {
  EDIT = 'edit',
  ADD = 'add',
  DELETE = 'delete',
}

export enum ContactDetailsEditAttributesEnum {
  NAME = 'name',
  EMAIL = 'email',
  PHONE = 'phone',
  TWITTER = 'twitter',
  TWITTER_DM = 'twitter_dm',
  CUSTOM = 'custom',
  DESCRIPTION = 'description',
  LINK = 'link',
  COMPANY_CUSTOM_FIELD = 'company_custom_field',
  CONTACTS = 'contacts',
  DOMAINS = 'domains',
  ACCOUNT_NAME = 'account_name',
}

export enum ContactDetailsTrackingErrorsEnum {
  DUPLICATE_ATTRIBUTE = 'duplicate_attribute',
  UNABLE_TO_SAVE = 'unable_to_save',
  MERGE_CONFLICT = 'merge_conflict',
}

export enum ContactDetailsHoverSectionsEnum {
  CONVERSATION = 'conversation',
  // TODO: implement tracking when summary section is added
  TEAMMATE_AVATAR = 'teammate_avatar',
  NOTE = 'note',
  DUPLICATE_WARNING = 'duplicate_warning',
}

export enum ContactDetailsAssignedContactTypesEnum {
  PRIVATE = 'private',
  COMPANY = 'company',
  AUTO = 'auto',
}

export enum ContactDetailsTrackingDisplayContextOptionsEnum {
  CONTACT = 'contact',
  ACCOUNT = 'account',
  DOMAIN = 'domain',
}

export enum ContactDetailsTrackingSwitchContactOptionsEnum {
  EXTERNAL = 'external',
  INTERNAL = 'internal_team',
}

/*
 * Interfaces.
 */

export interface ContactDetailsEditNoteTrackingPayload {
  namespace: string;
  contact_card_id: number;
  action: ContactDetailsCardNoteActionsEnum;
  access_setting: ContactDetailsCardNoteAccessSettingsEnum;
}

export interface ContactDetailsEditContactGroupPayload {
  namespace: string;
  contact_card_id: number;
  action: ContactDetailsCardGroupActionsEnum;
  is_new_group: boolean;
  contact_group_id: number;
}

export interface ContactDetailsClickPayload {
  namespace: string;
  contact_card_id: number;
  action: ContactDetailsClickActionsEnum;
  location: ContactDetailsClickLocationsEnum;
  display_context: ContactDetailsTrackingDisplayContextOptionsEnum;
}

export interface ContactDetailsAddToContactsPayload {
  namespace: string;
  contact_card_id: number;
  contact_type: ContactDetailsContactTypesEnum;
}

export interface ContactDetailsConvertContactTypePayload {
  namespace: string;
  contact_card_id: number;
  prior_contact_type: ContactDetailsContactTypesEnum;
  new_contact_type: ContactDetailsContactTypesEnum;
}

export interface ContactDetailsOpenConversationPayload {
  namespace: string;
  contact_card_id: number;
  section: ContactDetailsOpenConversationSectionsEnum | AccountDetailsOpenConversationSectionsEnum;
  display_context: ContactDetailsTrackingDisplayContextOptionsEnum;
}
export interface ContactDetailsEditPayload {
  namespace: string;
  contact_card_id: number;
  action: ContactDetailsEditActionsEnum | AccountDetailsEditActionsEnum;
  attribute: ContactDetailsEditAttributesEnum;
  display_context: ContactDetailsTrackingDisplayContextOptionsEnum;
}

export interface ContactDetailsMergePayload {
  namespace: string;
  contact_card_id: number;
  merge_target_contact_card_id: number;
}
export interface ContactDetailsViewPayload {
  namespace: string;
  contact_card_id: number | undefined; // The card may not exist
  contact_uid: string;
  if_multiple_contacts: boolean;
  assigned_contact_type: ContactDetailsAssignedContactTypesEnum | undefined;
  is_popout: boolean;
  if_duplicate_found: boolean;
  has_associated_account: boolean;
}

export interface ContactDetailsErrorDisplayedPayload {
  namespace: string;
  contact_card_id: number;
  error_type: ContactDetailsTrackingErrorsEnum;
}

export interface ContactDetailsHoverPayload {
  namespace: string;
  contact_card_id: number;
  section: ContactDetailsHoverSectionsEnum | AccountDetailsHoverOptionsEnum;
  display_context: ContactDetailsTrackingDisplayContextOptionsEnum;
}

export interface ContactDetailsSwitchTabsPayload {
  namespace: string;
  account_id: number | null;
  to_display_context: ContactDetailsTrackingDisplayContextOptionsEnum;
}

export interface ContactDetailsEditAccountPayload {
  contact_card_id: number;
  display_context: ContactDetailsTrackingDisplayContextOptionsEnum;
  action: ContactDetailsEditActionsEnum;
  is_new_account: boolean;
  has_domains: boolean;
  account_id: number;
}

export interface ContactDetailsSwitchContactsPayload {
  namespace: string;
  type: ContactDetailsTrackingSwitchContactOptionsEnum;
}

/*
 * Helpers.
 */

export function buildSwitchContactsPayload(
  namespace: string,
  isTeammate: boolean,
): ContactDetailsSwitchContactsPayload {
  return {
    namespace,
    type: isTeammate
      ? ContactDetailsTrackingSwitchContactOptionsEnum.INTERNAL
      : ContactDetailsTrackingSwitchContactOptionsEnum.EXTERNAL,
  };
}

export function buildNoteTrackingPayload(
  action: ContactDetailsCardNoteActionsEnum,
  card: CardModel,
  noteNamespace?: string,
): ContactDetailsEditNoteTrackingPayload {
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    access_setting: getNoteAccessSetting(noteNamespace),
    action,
  };
}

function getNoteAccessSetting(noteNamespace?: string): ContactDetailsCardNoteAccessSettingsEnum {
  if (noteNamespace && isTeammateNamespace(noteNamespace)) {
    return ContactDetailsCardNoteAccessSettingsEnum.PERSONAL;
  }
  if (noteNamespace && isTeamNamespace(noteNamespace)) {
    return ContactDetailsCardNoteAccessSettingsEnum.TEAM;
  }
  return ContactDetailsCardNoteAccessSettingsEnum.COMPANY;
}

export function buildContactGroupTrackingPayload(
  action: ContactDetailsCardGroupActionsEnum,
  card: CardModel,
  group: CardGroupModel,
  isNewGroup: boolean,
): ContactDetailsEditContactGroupPayload {
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    action,
    is_new_group: isNewGroup,
    contact_group_id: group.id,
  };
}

export function buildClickTrackingPayload(
  action: ContactDetailsClickActionsEnum,
  location: ContactDetailsClickLocationsEnum,
  card: CardModel,
  context: ContactDetailsTrackingDisplayContextOptionsEnum = ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT,
): ContactDetailsClickPayload {
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    action,
    location,
    display_context: context,
  };
}

export function buildAddToContactsTrackingPayload(
  contactType: ContactDetailsContactTypesEnum,
  card: CardModel,
): ContactDetailsAddToContactsPayload {
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    contact_type: contactType,
  };
}

export function buildConvertContactTypeTrackingPayload(
  priorContactType: ContactDetailsContactTypesEnum,
  newContactType: ContactDetailsContactTypesEnum,
  card: CardModel,
): ContactDetailsConvertContactTypePayload {
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    prior_contact_type: priorContactType,
    new_contact_type: newContactType,
  };
}

export function buildOpenConversationPayload(
  card: CardModel,
  section: ContactDetailsOpenConversationSectionsEnum | AccountDetailsOpenConversationSectionsEnum,
  context: ContactDetailsTrackingDisplayContextOptionsEnum = ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT,
): ContactDetailsOpenConversationPayload {
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    section,
    display_context: context,
  };
}
export function buildEditTrackingPayload(
  cardAttributeUpdateAction: CardAttributeUpdateAction,
  card: CardModel,
): ContactDetailsEditPayload | undefined {
  const action = computeEditAction(cardAttributeUpdateAction);
  const attribute = computeEditAttribute(cardAttributeUpdateAction);
  if (action && attribute) {
    return {
      namespace: card.namespace,
      contact_card_id: card.id,
      action,
      attribute,
      display_context: ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT,
    };
  }

  return undefined;
}

export function buildAccountDetailsEditTrackingPayload(
  card: CardModel,
  action: AccountDetailsEditActionsEnum,
  accountAttribute: AccountDetailsEditAttributesEnum,
): ContactDetailsEditPayload | undefined {
  const attribute = mapAccountDetailsToContactDetialsAttribute(accountAttribute);
  if (!attribute) {
    return undefined;
  }
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    action,
    attribute,
    display_context: ContactDetailsTrackingDisplayContextOptionsEnum.ACCOUNT,
  };
}

// Not all fields that are emitted for edits need to be tracked.
function mapAccountDetailsToContactDetialsAttribute(accountAttribute: AccountDetailsEditAttributesEnum) {
  switch (accountAttribute) {
    case AccountDetailsEditAttributesEnum.CUSTOM_FIELD:
      return ContactDetailsEditAttributesEnum.COMPANY_CUSTOM_FIELD;
    case AccountDetailsEditAttributesEnum.CONTACTS:
      return ContactDetailsEditAttributesEnum.CONTACTS;
    case AccountDetailsEditAttributesEnum.DESCRIPTION:
      return ContactDetailsEditAttributesEnum.DESCRIPTION;
    case AccountDetailsEditAttributesEnum.DOMAINS:
      return ContactDetailsEditAttributesEnum.DOMAINS;
    case AccountDetailsEditAttributesEnum.NAME:
      return ContactDetailsEditAttributesEnum.ACCOUNT_NAME;
    default:
      return undefined;
  }
}

function computeEditAction(cardAttributeUpdateAction: CardAttributeUpdateAction) {
  const actionTypeString = cardAttributeUpdateAction.type.toString().toLowerCase();
  if (actionTypeString.includes('add')) {
    return ContactDetailsEditActionsEnum.ADD;
  }

  if (actionTypeString.includes('delete')) {
    return ContactDetailsEditActionsEnum.DELETE;
  }

  if (actionTypeString.includes('update')) {
    return ContactDetailsEditActionsEnum.EDIT;
  }

  return undefined;
}

const contactSourceToEditAttributeMap: {[k in ContactSourcesEnum]?: ContactDetailsEditAttributesEnum} = {
  [ContactSourcesEnum.EMAIL]: ContactDetailsEditAttributesEnum.EMAIL,
  [ContactSourcesEnum.PHONE]: ContactDetailsEditAttributesEnum.PHONE,
  [ContactSourcesEnum.TWITTER]: ContactDetailsEditAttributesEnum.TWITTER,
  [ContactSourcesEnum.TWITTER_DM]: ContactDetailsEditAttributesEnum.TWITTER_DM,
  [ContactSourcesEnum.CUSTOM]: ContactDetailsEditAttributesEnum.CUSTOM,
  [ContactSourcesEnum.FACEBOOK]: undefined,
};

function computeContactSource(contactUid: string) {
  const [source] = parseContactUid(contactUid);
  return contactSourceToEditAttributeMap[source];
}

export function computeEditAttribute(cardAttributeUpdateAction: CardAttributeUpdateAction) {
  switch (cardAttributeUpdateAction.type) {
    case CardAttributeUpdateActionsEnum.UPDATE_NAME:
      return ContactDetailsEditAttributesEnum.NAME;

    case CardAttributeUpdateActionsEnum.ADD_DESCRIPTION:
    case CardAttributeUpdateActionsEnum.UPDATE_DESCRIPTION:
    case CardAttributeUpdateActionsEnum.DELETE_DESCRIPTION:
      return ContactDetailsEditAttributesEnum.DESCRIPTION;

    case CardAttributeUpdateActionsEnum.ADD_LINK:
    case CardAttributeUpdateActionsEnum.UPDATE_LINK:
    case CardAttributeUpdateActionsEnum.DELETE_LINK:
      return ContactDetailsEditAttributesEnum.LINK;

    case CardAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE:
    case CardAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE:
    case CardAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE:
      return ContactDetailsEditAttributesEnum.COMPANY_CUSTOM_FIELD;

    case CardAttributeUpdateActionsEnum.ADD_CONTACT:
    case CardAttributeUpdateActionsEnum.UPDATE_CONTACT:
    case CardAttributeUpdateActionsEnum.DELETE_CONTACT:
      return computeContactSource(cardAttributeUpdateAction.payload.contactUid);

    default:
      return undefined;
  }
}

export function buildContactMergePayload(
  namespace: string,
  mainCardId: number,
  targetCardId: number,
): ContactDetailsMergePayload {
  return {
    namespace,
    contact_card_id: mainCardId,
    merge_target_contact_card_id: targetCardId,
  };
}

export function buildErrorDisplayedTrackingPayload(
  errorType: ContactDetailsErrorTypesEnum,
  card: CardModel,
): ContactDetailsErrorDisplayedPayload | null {
  const mappedError = mapErrorTypeToTrackingErrorType(errorType);
  if (!mappedError) {
    return null;
  }
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    error_type: mappedError,
  };
}

function mapErrorTypeToTrackingErrorType(
  errorType: ContactDetailsErrorTypesEnum,
): ContactDetailsTrackingErrorsEnum | null {
  switch (errorType) {
    case ContactDetailsErrorTypesEnum.HANDLE_TAKEN:
      return ContactDetailsTrackingErrorsEnum.DUPLICATE_ATTRIBUTE;
    case ContactDetailsErrorTypesEnum.MERGE_FAILED:
      return ContactDetailsTrackingErrorsEnum.MERGE_CONFLICT;
    case ContactDetailsErrorTypesEnum.DETAIL_UPDATE_FAILED:
      return ContactDetailsTrackingErrorsEnum.UNABLE_TO_SAVE;
    default:
      return null;
  }
}

export function buildContactDetailsHoverPayload(
  card: CardModel,
  section: ContactDetailsHoverSectionsEnum | AccountDetailsHoverOptionsEnum,
  context: ContactDetailsTrackingDisplayContextOptionsEnum = ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT,
): ContactDetailsHoverPayload {
  return {
    namespace: card.namespace,
    contact_card_id: card.id,
    section,
    display_context: context,
  };
}

export function buildViewTrackingPayload({
  namespace,
  cardId,
  contactUid,
  hasMultipleContacts,
  assignedCardType,
  hasDuplicateCard,
  isPopout,
  hasAssociatedAccount,
}: ContactDetailsViewEvent): ContactDetailsViewPayload | undefined {
  return {
    namespace,
    contact_card_id: cardId,
    contact_uid: contactUid,
    if_multiple_contacts: hasMultipleContacts,
    assigned_contact_type: assignedCardType,
    is_popout: isPopout,
    if_duplicate_found: hasDuplicateCard,
    has_associated_account: hasAssociatedAccount,
  };
}

export function computeAssignedCardType(card: CardModel | undefined) {
  if (!card) {
    return undefined;
  }

  if (isAutoGlobalCard(card)) {
    return ContactDetailsAssignedContactTypesEnum.AUTO;
  }
  if (isEnhancedGlobalCard(card)) {
    return ContactDetailsAssignedContactTypesEnum.COMPANY;
  }
  if (isEnhancedPrivateCard(card)) {
    return ContactDetailsAssignedContactTypesEnum.PRIVATE;
  }

  return undefined;
}

export function buildSwitchTabsPayload(
  namespace: string,
  tab: ContactDetailsTrackingDisplayContextOptionsEnum,
  accountId?: number,
): ContactDetailsSwitchTabsPayload {
  return {
    namespace,
    account_id: accountId ?? null,
    to_display_context: tab,
  };
}

export function buildEditAccountPayload(
  cardId: number,
  account: AccountModel,
  context: ContactDetailsTrackingDisplayContextOptionsEnum,
  action: ContactDetailsEditActionsEnum,
  isNewAccount: boolean = false,
): ContactDetailsEditAccountPayload {
  return {
    contact_card_id: cardId,
    display_context: context,
    action,
    is_new_account: isNewAccount,
    has_domains: account.domains.length > 0,
    account_id: account.id,
  };
}

/*
 * Constants.
 */

export enum ContactDetailsEventTypesEnum {
  VIEW = 'view',
  EDIT = 'edit',
  CREATE_CARD_GROUP = 'create_card_group',
  EDIT_CARD_GROUP = 'edit_card_group',
  ADD_TO_CONTACTS = 'add_to_contacts',
  CONVERT_CONTACT_TYPE = 'convert_contact_type',
  EDIT_ACCOUNT = 'edit_account',
}

/*
 * Event Types.
 */

interface ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum;
}

export interface ContactDetailsViewEvent extends ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum.VIEW;
  namespace: string;
  cardId?: number;
  contactUid: string;
  hasMultipleContacts: boolean;
  assignedCardType: ContactDetailsAssignedContactTypesEnum | undefined;
  hasDuplicateCard: boolean;
  isPopout: boolean;
  hasAssociatedAccount: boolean;
}

export interface ContactDetailsEditEvent extends ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum.EDIT;
  cardAttributeUpdateAction: CardAttributeUpdateAction;
  card: CardModel;
}

export interface ContactDetailsCreateCardGroupEvent extends ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum.CREATE_CARD_GROUP;
  card: CardModel;
  cardGroup: CardGroupModel;
}

export interface ContactDetailsEditCardGroupEvent extends ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum.EDIT_CARD_GROUP;
  card: CardModel;
  cardGroup: CardGroupModel;
  action: ContactDetailsCardGroupActionsEnum;
}

export interface ContactDetailsConvertContactTypeEvent extends ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum.CONVERT_CONTACT_TYPE;
  card: CardModel;
  priorContactType: ContactDetailsContactTypesEnum;
  newContactType: ContactDetailsContactTypesEnum;
}

export interface ContactDetailsAddToContactsEvent extends ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum.ADD_TO_CONTACTS;
  namespace: string;
  cardId: number;
  contactType: ContactDetailsContactTypesEnum;
}

export interface ContactDetailsEditAccountEvent extends ContactDetailsEvent {
  type: ContactDetailsEventTypesEnum.EDIT_ACCOUNT;
  cardId: number;
  account: AccountModel;
  context: ContactDetailsTrackingDisplayContextOptionsEnum;
  action: ContactDetailsEditActionsEnum;
  namespace: string;
}

/*
 * Union of Events and Handler.
 */

export type ContactDetailsTrackingEvent =
  | ContactDetailsViewEvent
  | ContactDetailsEditEvent
  | ContactDetailsAddToContactsEvent
  | ContactDetailsConvertContactTypeEvent
  | ContactDetailsEditAccountEvent
  | ContactDetailsCreateCardGroupEvent
  | ContactDetailsEditCardGroupEvent;

export type ContactDetailsTrackEventHandler = (event: ContactDetailsTrackingEvent) => void;

/*
 * Event Creators.
 */

export function buildContactDetailsViewEvent(
  namespace: string,
  cardId: number | undefined,
  contactUid: string,
  hasMultipleContacts: boolean,
  assignedCardType: ContactDetailsAssignedContactTypesEnum | undefined,
  hasDuplicateCard: boolean,
  isPopout: boolean,
  hasAssociatedAccount: boolean,
): ContactDetailsViewEvent {
  return {
    type: ContactDetailsEventTypesEnum.VIEW,
    namespace,
    cardId,
    contactUid,
    hasMultipleContacts,
    assignedCardType,
    hasDuplicateCard,
    isPopout,
    hasAssociatedAccount,
  };
}

export function buildContactDetailsEditEvent(
  cardAttributeUpdateAction: CardAttributeUpdateAction,
  card: CardModel,
): ContactDetailsEditEvent {
  return {
    type: ContactDetailsEventTypesEnum.EDIT,
    cardAttributeUpdateAction,
    card,
  };
}

export function buildContactDetailsCreateCardGroupEvent(
  card: CardModel,
  cardGroup: CardGroupModel,
): ContactDetailsCreateCardGroupEvent {
  return {
    type: ContactDetailsEventTypesEnum.CREATE_CARD_GROUP,
    card,
    cardGroup,
  };
}

export function buildContactDetailsEditCardGroupEvent(
  action: ContactDetailsCardGroupActionsEnum,
  card: CardModel,
  cardGroup: CardGroupModel,
): ContactDetailsEditCardGroupEvent {
  return {
    type: ContactDetailsEventTypesEnum.EDIT_CARD_GROUP,
    action,
    card,
    cardGroup,
  };
}

export function buildContactDetailsAddToContactsEvent(
  card: CardModel,
  contactType: ContactDetailsContactTypesEnum,
): ContactDetailsAddToContactsEvent {
  return {
    type: ContactDetailsEventTypesEnum.ADD_TO_CONTACTS,
    cardId: card.id,
    contactType,
    namespace: card.namespace,
  };
}

export function buildContactDetailsConvertContactTypeEvent(
  priorContactType: ContactDetailsContactTypesEnum,
  newContactType: ContactDetailsContactTypesEnum,
  card: CardModel,
): ContactDetailsConvertContactTypeEvent {
  return {
    type: ContactDetailsEventTypesEnum.CONVERT_CONTACT_TYPE,
    priorContactType,
    newContactType,
    card,
  };
}

export function buildContactDetailsEditAccountEvent(
  cardId: number,
  account: AccountModel,
  context: ContactDetailsTrackingDisplayContextOptionsEnum,
  action: ContactDetailsEditActionsEnum,
  namespace: string,
): ContactDetailsEditAccountEvent {
  return {
    type: ContactDetailsEventTypesEnum.EDIT_ACCOUNT,
    cardId,
    account,
    context,
    action,
    namespace,
  };
}

export function ContactDetailsTrackingHandler(event: ContactDetailsTrackingEvent) {
  switch (event.type) {
    case ContactDetailsEventTypesEnum.VIEW: {
      trackInterfaceEvent(InterfaceEventsEnum.P4_CONTACT_DETAILS_VIEW, buildViewTrackingPayload(event));
      break;
    }
    case ContactDetailsEventTypesEnum.EDIT: {
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT,
        buildEditTrackingPayload(event.cardAttributeUpdateAction, event.card),
      );
      break;
    }
    case ContactDetailsEventTypesEnum.CREATE_CARD_GROUP: {
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT_CONTACT_GROUP,
        buildContactGroupTrackingPayload(
          ContactDetailsCardGroupActionsEnum.ADD,
          event.card,
          event.cardGroup,
          true,
        ),
      );
      break;
    }
    case ContactDetailsEventTypesEnum.EDIT_CARD_GROUP: {
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT_CONTACT_GROUP,
        buildContactGroupTrackingPayload(event.action, event.card, event.cardGroup, false),
      );
      break;
    }

    case ContactDetailsEventTypesEnum.ADD_TO_CONTACTS: {
      trackInterfaceEvent(InterfaceEventsEnum.P4_CONTACT_DETAILS_ADD_TO_CONTACTS, {
        namespace: event.namespace,
        contact_card_id: event.cardId,
        contact_type: event.contactType,
      });
      break;
    }

    case ContactDetailsEventTypesEnum.CONVERT_CONTACT_TYPE: {
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_CONVERT_CONTACT_TYPE,
        buildConvertContactTypeTrackingPayload(event.priorContactType, event.newContactType, event.card),
      );
      break;
    }
    case ContactDetailsEventTypesEnum.EDIT_ACCOUNT: {
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT_ACCOUNT,
        buildEditAccountPayload(event.cardId, event.account, event.context, event.action),
      );
      break;
    }
    default:
    // Do not track anything
  }
}
